import {FC, useEffect, useMemo} from "react";
import {
    Airport, AirportWithTimePost,
    EventPost,
    User
} from "../../../../types/domain.ts";
import {Descriptions, DescriptionsProps, Flex, Select, Tooltip, Typography} from "antd";
import {useUsers} from "../../../../queries/useUsers.ts";
import {useOwnProfile} from "../../../../stores/OwnProfileStore.ts";
import styles from './ApprovalsSelector.module.css';
import {useAirports} from "../../../../queries/useAirports.ts";
import {MinusCircleOutlined} from "@ant-design/icons";
import {isRegionManager} from "../../../../utils/userTypeGuards.ts";

type Props = {
    value?: EventPost['custom_approvers_ids'];
    onChange?: (value: EventPost['custom_approvers_ids']) => void;
    selectedAirports: AirportWithTimePost[];
    regionId: number;
}

export const ApprovalsSelector: FC<Props> = ({ value, onChange, selectedAirports, regionId }) => {
    const { profile } = useOwnProfile();
    const { data: users } = useUsers();
    const { data: airports } = useAirports();

    const regionManager = useMemo(() => {
        if(!profile || isRegionManager(profile)) return;
        return users?.find((user) => isRegionManager(user) && user.region?.id === regionId);
    }, [profile, users, regionId]);

    const firManagers = useMemo(() => {
        if(!airports || !users) return [];
        const curatedAirports = selectedAirports.filter(v => v);

        const uniqueAirportIcaos = Array.from(new Set(curatedAirports.map(({ airport_id }) => airport_id)));
        const uniqueAirports = uniqueAirportIcaos.map((icao) => airports.find(({ id }) => id === icao)).filter(v => v && v.fir) as Airport[];
        const notOwnRegion = uniqueAirports.filter(({ fir }) => fir!.region.id !== regionId);

        const uniqueRegionIcaos = Array.from(new Set(notOwnRegion.map(({ fir }) => fir!.region.id)));

        return uniqueRegionIcaos.map((icao) => users.find((user) => isRegionManager(user) && user.region.id === icao)).filter(v => v) as (User & { role: 'region_manager' })[];
    }, [selectedAirports, airports, users, profile, regionId]);

    useEffect(() => {
        if(!value?.length) return;

        // filter custom values to not include any auto-generated values
        onChange?.(value?.filter((v) => (!regionManager || v !== regionManager.id) && !firManagers.find(({ id }) => v === id)));
    }, [regionManager, firManagers]);

    const descriptionsItems: DescriptionsProps['items'] = [
        ...(regionManager ? [{
            key: 'region_manager',
            label: 'Ивент-менеджер моего региона',
            children: `${regionManager.first_name} ${regionManager.last_name}`
        }] : []),
        ...(firManagers.length || value?.length ? [{
            key: 'fir_managers',
            label: 'Ивент-менеджеры участвующих регионов',
            children: (
                <Flex vertical>
                    {firManagers.map((user) => (
                        <Flex gap={8}>
                            <Typography.Text key={user.id}>{user.first_name} {user.last_name} ({user.region.name})</Typography.Text>
                            <Tooltip title="Удаление невозможно, пока аэропорт этого региона добавлен справа">
                                <MinusCircleOutlined
                                    disabled
                                    className={styles.disabledDelete}
                                />
                            </Tooltip>
                        </Flex>
                    ))}
                    {value?.map((id) => {
                        const user = users?.find((user) => user.id === id);
                        if(!user || !isRegionManager(user)) return null;
                        return (
                            <Flex gap={8}>
                                <Typography.Text key={id}>{user.first_name} {user.last_name} ({user.region.name})</Typography.Text>
                                <MinusCircleOutlined
                                    onClick={() => {
                                        onChange?.(value?.filter((u) => u !== id) || [])
                                    }}
                                />
                            </Flex>
                        );
                    })}
                </Flex>
            )
        }] : [])
    ]

    return (
        <Flex vertical gap={16}>
            <Select
                showSearch
                optionFilterProp="label"
                value={null}
                placeholder="Выберите, чтобы добавить"
                options={users?.filter(({ id, role }) =>
                    !value?.includes(id) &&
                    (!regionManager || id !== regionManager.id) &&
                    !firManagers.find((m) => m.id === id) &&
                    role === 'region_manager' &&
                    id !== profile?.id
                ).map((user) => ({
                    label: `${user.first_name} ${user.last_name} (${isRegionManager(user) && user.region.name})`,
                    value: user.id
                }))}
                onChange={(v) => {
                    onChange?.(Array.from(new Set([
                        ...(value || []),
                        v!
                    ])))
                }}
            />
            <Descriptions
                column={1}
                layout="vertical"
                items={descriptionsItems}
                className={styles.descriptions}
            />
        </Flex>
    )
}